import * as React from 'react'

import { observer } from 'mobx-react'

import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import {
  IResourceLinkFieldResult,
  useEquipmentSensorLinkField,
  useSensorEquipmentLinkField,
} from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import useLocationResources from '~/client/src/shared/hooks/useLocationResources'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import useSensors from '~/client/src/shared/hooks/useSensors'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRuntimeContext } from '~/client/src/shared/resources/definitions'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

import { Loader } from '../Loader'
import { PanelSections } from '../PanelSections/PanelSections'
import { ResourceEdit } from '../ResourceViewEdit'
import { ResourceEditRecord } from '../ResourceViewEdit/ResourceEditContext'
import { ResourceEditDetailsBody } from '../ResourceViewEdit/ResourceEditDetailsBody'
import { buildResourceTagSections } from '../ResourceViewEdit/resourceEditSectionBuilders'
import { useLinkedResourceEditor } from './useLinkedResourceEditor'

interface IProps {
  open: boolean
  recordId: string | null
  projectId?: string
  resourceTypeId: string
  onClose: () => void
  shell: (children: React.ReactNode) => React.ReactElement
}

interface IInnerProps extends IProps {
  runtimeContext: IResourceRuntimeContext
}

interface IBodyProps extends IInnerProps {
  record: IResourceRecord | null
  loading: boolean
  save: (recordId: string | null, formData: Record<string, any>) => Promise<any>
  schema: ResourceSchema
  // Only equipment and StruxTags link to each other; other types have no link field.
  linkField?: IResourceLinkFieldResult
  openedLinkedRecordId?: string | null
  openLinkedRecord?: () => void
  closeLinkedRecord?: () => void
}

function toEditableRecord(
  record: IResourceRecord | null,
): ResourceEditRecord | null {
  if (!record) return null

  return {
    ...record,
    code: record.code ?? null,
    isDeleted: record.isDeleted ?? false,
    templateId: record.templateId ?? null,
  }
}

let linkedFormCount = 0

const LinkedResourceEditBody: React.FC<IBodyProps> = ({
  open,
  recordId,
  record,
  loading,
  projectId,
  resourceTypeId,
  runtimeContext,
  onClose,
  save,
  schema,
  shell,
  linkField,
  openedLinkedRecordId,
  openLinkedRecord,
  closeLinkedRecord,
}) => {
  const editableRecord = React.useMemo(() => toEditableRecord(record), [record])
  // Cards nest (a linked equipment or StruxTag opens over the card that links it), and Save
  // targets its form by id, so each mounted card needs its own.
  const formId = React.useRef(
    `dynamic-form-linked-${++linkedFormCount}`,
  ).current

  const qrCodeUrl = React.useMemo(() => {
    if (!recordId) return null
    return `${window.location.origin}/resource/${resourceTypeId}/${recordId}`
  }, [recordId, resourceTypeId])

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      await save(recordId, formData)
    },
    [save, recordId],
  )

  if (!open || !recordId) return null

  if (!record) {
    return shell(
      <>
        <PanelSections.Header onClose={onClose} />
        <PanelSections.Content>
          {loading ? (
            <Loader />
          ) : (
            <div className="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:p-6">
              <span className="tw:text-muted-foreground">
                {Localization.translator.noResults}
              </span>
            </div>
          )}
        </PanelSections.Content>
      </>,
    )
  }

  return (
    <>
      {shell(
        <ResourceEdit.Root
          recordId={recordId}
          record={editableRecord}
          resourceTypeId={resourceTypeId}
          runtimeContext={runtimeContext}
          formId={formId}
          onClose={onClose}
          onSave={handleSave}
          qrCodeUrl={qrCodeUrl}
          resourceSchema={schema}
        >
          <ResourceEditDetailsBody
            isCreationMode={false}
            linkField={linkField}
            onOpenLinked={openLinkedRecord}
            sections={buildResourceTagSections({
              projectId,
              infuseId: linkField?.linkRootProps.linkedItem?.infuseId,
              showStruxTagSections: !!linkField,
            })}
          />
        </ResourceEdit.Root>,
      )}
      {linkField && (
        <LinkedResourceEdit
          open={!!openedLinkedRecordId}
          recordId={openedLinkedRecordId}
          projectId={projectId}
          resourceTypeId={linkField.targetResourceTypeId}
          onClose={closeLinkedRecord}
          shell={shell}
        />
      )}
    </>
  )
}

const SensorLinkedResourceEdit: React.FC<IInnerProps> = observer(props => {
  const { records, loading, save, schema } = useSensors({
    projectId: props.projectId,
  })
  const record = React.useMemo(() => {
    if (!props.recordId) return null
    return records.find(item => item.id === props.recordId) || null
  }, [props.recordId, records])
  const linkField = useSensorEquipmentLinkField({
    projectId: props.projectId,
    sourceRecordId: props.recordId,
    sourceRecord: record,
  })
  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  return (
    <LinkedResourceEditBody
      {...props}
      record={record}
      loading={loading}
      save={save}
      schema={schema}
      linkField={linkField}
      openedLinkedRecordId={openedLinkedRecordId}
      openLinkedRecord={openLinkedRecord}
      closeLinkedRecord={closeLinkedRecord}
    />
  )
})

const EquipmentLinkedResourceEdit: React.FC<IInnerProps> = observer(props => {
  const { records, loading, save, schema } = useEquipmentResources({
    projectId: props.projectId,
  })
  const record = React.useMemo(() => {
    if (!props.recordId) return null
    return records.find(item => item.id === props.recordId) || null
  }, [props.recordId, records])
  const linkField = useEquipmentSensorLinkField({
    projectId: props.projectId,
    sourceRecordId: props.recordId,
    sourceRecord: record,
  })
  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  return (
    <LinkedResourceEditBody
      {...props}
      record={record}
      loading={loading}
      save={save}
      schema={schema}
      linkField={linkField}
      openedLinkedRecordId={openedLinkedRecordId}
      openLinkedRecord={openLinkedRecord}
      closeLinkedRecord={closeLinkedRecord}
    />
  )
})

const LocationLinkedResourceEdit: React.FC<IInnerProps> = observer(props => {
  const { records, loading, save, schema } = useLocationResources({
    projectId: props.projectId,
  })
  const record = React.useMemo(
    () => records.find(item => item.id === props.recordId) || null,
    [props.recordId, records],
  )

  return (
    <LinkedResourceEditBody
      {...props}
      record={record}
      loading={loading}
      save={save}
      schema={schema}
    />
  )
})

const MaterialLinkedResourceEdit: React.FC<IInnerProps> = observer(props => {
  const { records, loading, save, schema } = useMaterialResources({
    projectId: props.projectId,
  })
  const record = React.useMemo(
    () => records.find(item => item.id === props.recordId) || null,
    [props.recordId, records],
  )

  return (
    <LinkedResourceEditBody
      {...props}
      record={record}
      loading={loading}
      save={save}
      schema={schema}
    />
  )
})

export const LinkedResourceEdit: React.FC<IProps> = props => {
  const runtimeContext = useResourceRuntimeContext()
  if (!props.open || !props.recordId) return null

  const innerProps: IInnerProps = { ...props, runtimeContext }

  if (props.resourceTypeId === ResourceTypeId.SENSORS) {
    return <SensorLinkedResourceEdit {...innerProps} />
  }

  if (props.resourceTypeId === ResourceTypeId.OFFLOADING_EQUIPMENT) {
    return <EquipmentLinkedResourceEdit {...innerProps} />
  }

  if (props.resourceTypeId === ResourceTypeId.LOCATIONS) {
    return <LocationLinkedResourceEdit {...innerProps} />
  }

  if (props.resourceTypeId === ResourceTypeId.MATERIALS) {
    return <MaterialLinkedResourceEdit {...innerProps} />
  }

  return null
}
